import { clientDayKey, clientTime, shortDateTime, shortDay } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';
import { FORKLIFT_FILTER_DEFAULT } from './forklift';
import { requirementParts } from './orderCargo';

/**
 * Bank zleceń według makiety FTL p. Mariusza (13.10.2026, pkt 9.3) — Etap 1,
 * decyzja Bartka 15.10.2026. Przełączniki, klucze filtrów, opcje i tekst
 * komórek; komponenty: `BankFtlCells.tsx`, `BankFtlFilters.tsx`,
 * `BankViewsMenu.tsx`, `RowAssignDialog.tsx`, `MyFleetBank.tsx`.
 *
 * ## Mieszczenie przy 1366 px (decyzja 08.10.2026)
 *
 * Lista ma już 11–12 kolumn i mieści się na styk, więc kolumny z makiety
 * (typ pojazdu, wymagania, stawka, liczba punktów, okno, warianty
 * z autorami) **nie dochodzą jako nowe kolumny**, tylko jako treść
 * istniejących komórek: okno od–do w „Załadunku” (dwie linie zamiast
 * jednej), punkty, typ i wymagania pod relacją, warianty w „Pojeździe”
 * zlecenia bez auta, stawka pod kontrahentem. Szerokości komórek bez zmian
 * (relacja 160 px, pojazd 150 px — warianty 120 px, kontrahent 150 px przy
 * 1366 px) — tekst dłuższy niż komórka ucina się wielokropkiem, pełny jest
 * w dymku. Liczba wariantów znika ze „Znaczników”, gdy warianty stoją
 * w „Pojeździe”. Pełny pomiar szerokości robi e2e (`checkListFits`).
 */

// ── Przełączniki (lustro rejestru API, `feature-flags.ts`) ──────────────

/** Okno od–do, punkty, typ, wymagania, warianty z autorami, stawka. */
export const FEATURE_BANK_COLUMNS = 'bankFtlColumns';
/** Nowe filtry listy i kilka stanów naraz. */
export const FEATURE_BANK_FILTERS = 'bankFtlFilters';
/** Domyślne widoki ról i „Widok domyślny”. */
export const FEATURE_BANK_DEFAULT_VIEWS = 'bankRoleDefaultViews';
/** Zapisane widoki użytkownika. */
export const FEATURE_BANK_SAVED_VIEWS = 'bankSavedViews';
/** „Zlecenia mojej floty” w menu. */
export const FEATURE_BANK_MY_FLEET = 'bankMyFleetView';
/**
 * Pozycja menu „Zlecenia mojej floty” — i tytuł tej listy (tytuł ekranu
 * z pozycji menu, jak `menuLabel` przy pozostałych listach).
 */
export const MY_FLEET_TITLE = 'Zlecenia mojej floty';
/** „Obsadź…” w wierszu. */
export const FEATURE_BANK_ROW_ASSIGN = 'bankRowAssign';
/**
 * Osobne kolumny „Stawka” i „Typ pojazdu” (odpowiedź z 21.10.2026 na pytanie
 * 20; S-24.10-13). Działa razem z `bankFtlColumns` — bez niego lista jest
 * w układzie sprzed makiety i obu kolumn nie ma.
 */
export const FEATURE_BANK_RATE_TYPE_COLUMNS = 'bankRateTypeColumns';

// ── Klucze filtrów — kontrakt z API (`user-offers/bank-ftl.ts`) ────────

export const LOADING_WITHIN_DAYS_FILTER = 'loadingWithinDays';
export const LOADING_DAY_FROM_FILTER = 'loadingDayFrom';
export const LOADING_DAY_TO_FILTER = 'loadingDayTo';
export const VEHICLE_TYPE_FILTER = 'vehicleType';
export const REQUIREMENT_FILTER = 'requirement';
export const CARETAKER_FILTER = 'vehicleCaretaker';
export const NEEDS_ASSIGNMENT_FILTER = 'needsAssignment';
/** Wartość klucza „Znaczniki” (`forklift`). */
export const MARKERS_FILTER_WITHOUT_VARIANT = 'withoutVariant';
/** Stare klucze, które dopiero teraz mają pole w interfejsie. */
export const FROM_COUNTRY_FILTER = 'fromCountry';
export const TO_COUNTRY_FILTER = 'toCountry';

/** Makieta: „do jutra / w ciągu 3 dni / w ciągu 7 dni”; 14 — dla planujących dalej. */
export const LOADING_WITHIN_CHOICES = [
  { id: 1, name: 'do jutra (24 h)' },
  { id: 3, name: 'w ciągu 3 dni' },
  { id: 7, name: 'w ciągu 7 dni' },
  { id: 14, name: 'w ciągu 14 dni' },
];

/** Grupy wymaganego typu — wartości to dane API (`VEHICLE_TYPE_GROUPS`). */
export const VEHICLE_TYPE_CHOICES = [
  { id: 'JUMBO', name: 'JUMBO' },
  { id: 'MEGA', name: 'MEGA' },
  { id: 'STANDARD', name: 'STANDARD' },
  { id: 'PARTIAL', name: 'Częściowe (K × LDM)' },
];

/** Wymagania — wartości to dane API (`REQUIREMENTS`). */
export const REQUIREMENT_CHOICES = [
  { id: 'adr', name: 'ADR' },
  { id: 'fixed', name: 'Fix (sztywny termin)' },
  { id: 'forklift', name: 'Wózek' },
];

/**
 * Kraje w filtrze — kody ISO jak w danych TMS (`fromCountry`), nazwa po
 * polsku z przeglądarki. Lista krajów, w których Marathon jeździ (Europa);
 * kod spoza niej da się nadal wyfiltrować adresem — API przyjmuje każdy.
 */
const COUNTRY_CODES = [
  'PL', 'DE', 'NL', 'BE', 'LU', 'FR', 'IT', 'ES', 'PT', 'AT', 'CH', 'CZ',
  'SK', 'HU', 'SI', 'HR', 'RO', 'BG', 'GR', 'DK', 'SE', 'NO', 'FI', 'EE',
  'LV', 'LT', 'GB', 'IE', 'RS', 'BA', 'UA', 'TR',
];

function countryName(code: string): string {
  try {
    const names = new Intl.DisplayNames(['pl'], { type: 'region' });
    const name = names.of(code);
    return name && name !== code ? `${code} — ${name}` : code;
  } catch {
    return code;
  }
}

export const COUNTRY_CHOICES = COUNTRY_CODES.map((code) => ({ id: code, name: countryName(code) }));

/** Opcja „Bez wariantu” w „Znacznikach” — tylko z przełącznikiem filtrów. */
export const WITHOUT_VARIANT_CHOICE = { id: MARKERS_FILTER_WITHOUT_VARIANT, name: 'Bez wariantu' };

export function withWithoutVariantChoice<C extends { id: string; name: string }>(
  choices: readonly C[],
  on: boolean,
): Array<C | typeof WITHOUT_VARIANT_CHOICE> {
  return on ? [...choices, WITHOUT_VARIANT_CHOICE] : [...choices];
}

// ── Domyślne widoki ról ────────────────────────────────────────────────

/** Parametry listy w magazynie react-admina (`<zasób>.listParams`). */
export interface BankListParams {
  filter: Record<string, unknown>;
  displayedFilters: Record<string, boolean>;
  sort?: string;
  order?: 'ASC' | 'DESC';
}

/** Domyślny filtr listy (bez widoku roli) — ten sam co `filterDefaultValues`. */
export const PLAIN_LIST_PARAMS: BankListParams = {
  filter: { forklift: FORKLIFT_FILTER_DEFAULT },
  displayedFilters: {},
};

/**
 * Domyślny widok roli (makieta pkt 9.3): planista FTL — „Gotowe do
 * planowania” i „Wstępnie planowane” z załadunkiem w ciągu 7 dni; planista
 * główny — wymagające obsadzenia, też w 7 dniach (makieta, `views.js`:
 * `{ stan: 'do-obsadzenia', okres: '7' }`). Oba ułożone po załadunku
 * rosnąco, jak w makiecie. Inne role — `null` (zostaje widok działu albo
 * lista bez filtra, jak dotąd).
 */
export function roleDefaultListParams(role: string | null | undefined): BankListParams | null {
  const base = { sort: 'fromDate', order: 'ASC' as const };
  if (role === 'PLANNER_FTL') {
    return {
      ...base,
      filter: {
        planningStatus: ['ready', 'tentative'],
        [LOADING_WITHIN_DAYS_FILTER]: 7,
        forklift: FORKLIFT_FILTER_DEFAULT,
      },
      displayedFilters: { [LOADING_WITHIN_DAYS_FILTER]: true },
    };
  }
  if (role === 'FTL_MASTER') {
    return {
      ...base,
      filter: {
        [NEEDS_ASSIGNMENT_FILTER]: true,
        [LOADING_WITHIN_DAYS_FILTER]: 7,
        forklift: FORKLIFT_FILTER_DEFAULT,
      },
      displayedFilters: { [NEEDS_ASSIGNMENT_FILTER]: true, [LOADING_WITHIN_DAYS_FILTER]: true },
    };
  }
  return null;
}

/** Wartość filtra stanu jako lista — zapamiętany pojedynczy stan też. */
export function asStatusList(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  return typeof value === 'string' && value !== '' ? [value] : [];
}

/** Lista z wyboru wielu stanów — pusta to brak filtra (stany żywe z API). */
export function statusListOrNothing(value: unknown): string[] | undefined {
  const list = asStatusList(value);
  return list.length > 0 ? list : undefined;
}

// ── Treść komórek ──────────────────────────────────────────────────────

type Loose = Record<string, unknown>;

const text = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

/**
 * Okno załadunku w dwóch liniach: „15.10, 08:00” i „do 16:00” (ten sam
 * dzień) albo „do 16.10, 16:00”. Bez końca okna — sama chwila początku;
 * drugiego krańca nie zgadujemy. Początek to `loadingDateFrom`, a bez niego
 * `fromDate` (data załadunku z TMS, po niej sortuje kolumna).
 */
export function loadingWindowLines(record: Loose): { start: string; end: string | null } {
  const from = text(record.loadingDateFrom) ?? text(record.fromDate);
  const to = text(record.loadingDateTo);
  if (!from) return { start: '—', end: null };
  const start = shortDateTime(from);
  if (!to || to === from) return { start, end: null };
  const sameDay = clientDayKey(new Date(from)) === clientDayKey(new Date(to));
  return { start, end: sameDay ? `do ${clientTime(to)}` : `do ${shortDay(to)}, ${clientTime(to)}` };
}

/** Krótka nazwa typu do komórki: „JUMBO”, „K 13,2 LDM”; wózek osobno. */
function shortTypeName(part: string): string {
  return part.replace(/ TRUCK$/, '');
}

/**
 * Linia pod relacją: liczba punktów, wymagany typ i wymagania — „3 pkt ·
 * JUMBO · wózek · ADR · fix”. Czego nie ma, tego nie piszemy; `null` = nic.
 * Typ to wymaganie zlecenia z TMS (`assignedVehicleType`), nie typ auta.
 */
export function requirementsLine(record: Loose): string | null {
  const parts: string[] = [];
  const points = typeof record.pointCount === 'number' ? record.pointCount : 0;
  if (points > 0) parts.push(`${points} pkt`);
  const type = requirementParts(record.assignedVehicleType);
  const vehicle = type.filter((p) => p !== 'FORKLIFT').map(shortTypeName);
  parts.push(...vehicle);
  if (type.includes('FORKLIFT')) parts.push(vehicle.length > 0 ? 'wózek' : 'czynność wózkowa');
  if (record.adr === true) parts.push('ADR');
  if (record.fixed === true) parts.push('fix');
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Linia pod relacją przy osobnej kolumnie „Typ pojazdu” (S-24.10-13): sama
 * liczba punktów — typu i wymagań pod relacją już nie powtarzamy. `null` =
 * nic (zlecenie bez punktów z TMS).
 */
export function pointsLine(record: Loose): string | null {
  const points = typeof record.pointCount === 'number' ? record.pointCount : 0;
  return points > 0 ? `${points} pkt` : null;
}

/**
 * Kolumna „Typ pojazdu” (S-24.10-13): wymagany typ w pierwszej linii
 * („JUMBO”, „K 13,2 LDM”, „czynność wózkowa”), wymagania w drugiej („wózek ·
 * ADR · fix”). Brak typu w danych — `type: null` (komórka pokaże „—”),
 * wymagania i tak, jeśli są. Ta sama reguła nazw co `requirementsLine`.
 */
export function vehicleTypeLines(record: Loose): { type: string | null; requirements: string | null } {
  const parts = requirementParts(record.assignedVehicleType);
  const vehicle = parts.filter((p) => p !== 'FORKLIFT').map(shortTypeName);
  const forklift = parts.includes('FORKLIFT');
  const type = vehicle.length > 0 ? vehicle.join(' · ') : forklift ? 'czynność wózkowa' : null;
  const requirements: string[] = [];
  if (forklift && vehicle.length > 0) requirements.push('wózek');
  if (record.adr === true) requirements.push('ADR');
  if (record.fixed === true) requirements.push('fix');
  return { type, requirements: requirements.length > 0 ? requirements.join(' · ') : null };
}

/** Dymek kolumny „Typ pojazdu” — pełne zdanie bez liczby punktów. */
export function vehicleTypeHint(record: Loose): string {
  const type = text(record.assignedVehicleType);
  const parts = requirementParts(record.assignedVehicleType);
  return [
    type ? `Wymagany typ pojazdu: ${type}.` : 'Wymagany typ pojazdu: brak w danych.',
    parts.includes('FORKLIFT') && parts.length > 1 ? 'Wymaga wózka.' : null,
    record.adr === true ? 'Zlecenie z ADR.' : null,
    record.fixed === true ? 'Fix — sztywny termin.' : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' ');
}

/**
 * Kolumna „Stawka” (S-24.10-13): kwota w pierwszej linii, waluta w drugiej
 * — tak kolumna jest wąska. Brak stawki albo zero — `null` („—”), nie
 * zgadujemy kwoty.
 */
export function rateParts(price: unknown, currency: unknown): { amount: string; currency: string | null } | null {
  const n = typeof price === 'number' ? price : typeof price === 'string' ? Number(price) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  return { amount: plNumber(n, 2), currency: text(currency) ?? null };
}

/** Pełne zdanie do dymka linii wymagań. */
export function requirementsHint(record: Loose): string {
  const points = typeof record.pointCount === 'number' ? record.pointCount : 0;
  const type = text(record.assignedVehicleType);
  return [
    points > 0 ? `Punkty trasy: ${points}.` : 'Brak punktów trasy z TMS.',
    type ? `Wymagany typ pojazdu: ${type}.` : 'Wymagany typ pojazdu: brak w danych.',
    record.adr === true ? 'Zlecenie z ADR.' : null,
    record.fixed === true ? 'Fix — sztywny termin.' : null,
  ]
    .filter((s): s is string => s !== null)
    .join(' ');
}

/** Stawka zlecenia: „1 850 EUR”; brak — `null` (komórka pokaże „—”). */
export function rateText(price: unknown, currency: unknown): string | null {
  const n = typeof price === 'number' ? price : typeof price === 'string' ? Number(price) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${plNumber(n, 2)} ${text(currency) ?? ''}`.trim();
}

/** Aktywny wariant z listy API (`listVariants`). */
export interface ListVariant {
  id: number;
  carId: number;
  registrationNumber: string | null;
  ftlCaretaker: string | null;
  addedBy: string | null;
  origin: string | null;
  createdAt: string;
}

export function listVariantsOf(record: Loose): { variants: ListVariant[]; count: number } {
  const variants = Array.isArray(record.listVariants) ? (record.listVariants as ListVariant[]) : [];
  const count = typeof record.listVariantCount === 'number' ? record.listVariantCount : variants.length;
  return { variants, count };
}

/** Numer auta wariantu; bez numeru w kartotece — `#id`. */
export const variantPlate = (v: Pick<ListVariant, 'registrationNumber' | 'carId'>) =>
  v.registrationNumber ?? `#${v.carId}`;

/** Czy zlecenie ma auto z floty — wtedy „Pojazd” pokazuje je, nie warianty. */
export const hasAssignedCar = (record: Loose) =>
  record.assignedCarId !== null && record.assignedCarId !== undefined;

/**
 * Czy „Pojazd” pokazuje warianty (zlecenie bez auta z floty, bez przewoźnika
 * i bez numeru z TMS, z aktywnym wariantem). Wtedy liczba wariantów znika ze
 * „Znaczników” — stoi w „Pojeździe”; przy obsadzonym zostaje.
 */
export function variantsShownInVehicleCell(record: Loose): boolean {
  if (hasAssignedCar(record) || !!record.carrier || !!record.assignedVehicleNumber) return false;
  return listVariantsOf(record).count > 0;
}

/** Stany, w których obsadza się z wariantu (jak `NEEDS_ASSIGNMENT_STATES` w API). */
const ROW_ASSIGN_STATES = ['ready', 'tentative'];

/**
 * Czy wiersz pokazuje „Obsadź…” (aktywne albo wyszarzone z powodem):
 * gotowe albo wstępnie planowane, bez auta, z co najmniej jednym aktywnym
 * wariantem. Prezlecenia nie obsadzamy (najpierw akceptacja —
 * `statusAfterCarAssignment` w API).
 */
export function showsRowAssign(record: Loose): boolean {
  return (
    typeof record.planningStatus === 'string' &&
    ROW_ASSIGN_STATES.includes(record.planningStatus) &&
    !hasAssignedCar(record) &&
    listVariantsOf(record).count > 0
  );
}

/**
 * Dlaczego „Obsadź…” jest wyszarzone: zlecenie z prezlecenia, którego nie ma
 * jeszcze w TMS (16.10.2026, odpowiedzi Marathonu/Euvic, pyt. 5: „finalne
 * przypisanie wymaga zlecenia transportowego w VIA TMS”). Powód liczy API
 * tą samą regułą co odmowę obsadzenia (`notInTmsRefusal` w wierszu listy);
 * warianty dodaje się dalej. To stan danych, nie uprawnienie — dlatego
 * wyszarzamy z powodem, zamiast ukrywać.
 */
export function rowAssignBlockedReason(record: Loose): string | null {
  return text(record.notInTmsRefusal) ?? null;
}

/**
 * Czy „Obsadź…” w wierszu jest aktywne. Ostatnie słowo i tak ma serwer.
 */
export function canAssignFromRow(record: Loose): boolean {
  return showsRowAssign(record) && rowAssignBlockedReason(record) === null;
}
